import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import SetupWizard from "@/components/recstudio/setup/SetupWizard";
import WorkspaceHeader from "@/components/recstudio/WorkspaceHeader";
import StepStrip from "@/components/recstudio/StepStrip";
import InstrumentalCard from "@/components/recstudio/InstrumentalCard";
import TrackList from "@/components/recstudio/TrackList";
import BigWaveform from "@/components/recstudio/BigWaveform";
import RecControls from "@/components/recstudio/RecControls";
import TakeCheckCard from "@/components/recstudio/TakeCheckCard";
import TakesPanel from "@/components/recstudio/TakesPanel";
import useRevoiceTakes from "@/components/recstudio/useRevoiceTakes";
import VocalSoundPanel from "@/components/recstudio/VocalSoundPanel";
import ExportPanel from "@/components/recstudio/ExportPanel";
import VoiceModelPanel from "@/components/recstudio/VoiceModelPanel";
import LyricPrompter from "@/components/recstudio/LyricPrompter";
import LyricsSource from "@/components/recstudio/LyricsSource";
import PunchBar from "@/components/recstudio/PunchBar";
import useRecSession from "@/components/recstudio/useRecSession";
import useTakeRecorder from "@/components/recstudio/useTakeRecorder";
import usePlayhead from "@/components/recstudio/usePlayhead";
import { INSTRUMENTAL, channelOf } from "@/components/recstudio/sessionEngine";
import { useClickTrack } from "@/components/recstudio/beatClick";
import { snapRange } from "@/lib/tempoKey";
import { ISSUE_TEXT } from "@/lib/audioAnalysis";
import { loadSetup } from "@/lib/recStudioApi";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function buildSteps(setup, doc, track, saveState) {
  const takes = doc.tracks.flatMap((t) => t.takes);
  const last = takes[takes.length - 1];
  const clean = last && !(last.check?.issues || []).some((c) => ISSUE_TEXT[c] && ISSUE_TEXT[c].level !== "info");
  return [
    { key: "mic", label: "Mic setup", done: Boolean(setup.ready), setup: true },
    { key: "phones", label: "Headphones & sync", done: Boolean(setup.ready), setup: true },
    { key: "music", label: "Music", done: Boolean(doc.instrumental), panel: "rec-section-music" },
    { key: "sound", label: "Vocal sound", done: Boolean(track?.preset), panel: "rec-section-sound" },
    { key: "record", label: "Record", done: takes.length > 0, panel: "rec-section-record" },
    { key: "check", label: "Take check", done: Boolean(clean), panel: "rec-section-record" },
    { key: "save", label: "Save & export", done: takes.length > 0 && saveState === "saved", panel: "rec-section-export" },
  ];
}

/** One recording session: music + vocal tracks, big waveform, plain controls, take check, vocal sound, export, AI voice. */
export default function Workspace({ sessionId }) {
  const [search, setSearch] = useSearchParams();
  const session = useRecSession(sessionId);
  const { doc, error, engine, syncing, saveState, change, updateTrack, save } = session;
  const [setup, setSetup] = useState(loadSetup);
  const [wizard, setWizard] = useState(() => search.get("setup") === "1" || (!loadSetup().ready && search.get("setup") !== "0"));
  const [selected, setSelected] = useState(null);
  const trackId = doc?.tracks.some((t) => t.id === selected) ? selected : doc?.tracks[0]?.id || null;
  const rec = useTakeRecorder({ session, trackId, setup });
  const revoice = useRevoiceTakes(session, rec.phase !== "idle");
  const ph = usePlayhead(engine);
  const [sel, setSel] = useState(null);
  const [preroll, setPreroll] = useState(3);
  const [lyricsOpen, setLyricsOpen] = useState(false);
  const tempo = doc?.instrumental?.tempo || null;
  useClickTrack(engine, tempo, Boolean(doc?.settings?.click) && setup.headphones === true && rec.phase === "recording", rec.startPos);
  useEffect(() => () => engine.pause(), [engine]);
  useEffect(() => setSel(null), [trackId]);
  useEffect(() => { if (rec.result?.punch) setSel(null); }, [rec.result]);

  if (error) return <CreatorShell testId="rec-studio-workspace" back="/create/studio" title="Hmm, that session isn't here" subtitle={error}><Link to="/create/studio" className="underline">See all sessions</Link></CreatorShell>;
  if (!doc) return <CreatorShell testId="rec-studio-workspace" back="/create/studio"><p className="flex items-center gap-2 text-white/60"><Loader2 className="h-5 w-5 animate-spin" /> Opening your session…</p></CreatorShell>;
  if (wizard) {
    return (
      <CreatorShell testId="rec-studio-setup-page" back="/create/studio" title="Recording setup" subtitle="Step 1: your microphone. Step 2: headphones, test sound and sync. Then you're ready to record.">
        <SetupWizard onDone={(next) => { if (next) { setSetup(next); toast.success("You're ready to record"); } setWizard(false); if (search.has("setup")) setSearch({}, { replace: true }); }} />
      </CreatorShell>
    );
  }

  const track = doc.tracks.find((t) => t.id === trackId) || null;
  const bufOf = (part) => { const i = channelOf(engine, doc.id, part); return i != null ? engine.channels[i].buffer : null; };
  const instBuf = bufOf(INSTRUMENTAL);
  const duration = Math.max(ph.duration, instBuf?.duration || 0);
  const busyRec = rec.phase !== "idle";
  const onSave = async () => { if (rec.result?.saveError) rec.retrySave(); await save(); toast.success("Session saved"); };
  const activeTake = track?.takes.find((t) => t.id === track.active_take_id) || null;
  const live = rec.phase === "recording" || rec.phase === "paused";
  const lyricTime = ph.playing || !live ? ph.time : rec.startPos + rec.elapsed;
  const onStep = (s) => (s.setup ? (busyRec ? null : setWizard(true)) : scrollTo(s.panel));
  const snap = Boolean(tempo) && doc.settings.snap !== false;
  const onSnap = (on) => { change((d) => ({ ...d, settings: { ...d.settings, snap: on } })); if (on && sel) setSel(snapRange(tempo, sel)); };

  return (
    <CreatorShell testId="rec-studio-workspace" back="/create/studio" wide>
      <WorkspaceHeader doc={doc} setup={setup} saveState={saveState} syncing={syncing} onTitle={(title) => change((d) => ({ ...d, title }))} onSetup={() => !busyRec && setWizard(true)} />
      <div className="mt-4"><StepStrip steps={buildSteps(setup, doc, track, saveState)} onStep={onStep} /></div>
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[300px_minmax(0,1fr)] 2xl:grid-cols-[300px_minmax(0,1fr)_320px]">
        <aside id="rec-section-music" className="min-w-0 space-y-4">
          <InstrumentalCard session={session} buffer={instBuf} duration={duration} />
          <TrackList session={session} selectedId={trackId} onSelect={setSelected} duration={duration} />
        </aside>
        <div id="rec-section-record" className="min-w-0 space-y-4">
          <LyricPrompter lyrics={doc.lyrics} musicEnd={duration} time={lyricTime} live={live} onSeek={ph.seek} onEdit={() => setLyricsOpen(true)} tempo={doc.instrumental?.tempo}
            onChange={(lyrics) => change((d) => ({ ...d, lyrics }))} />
          <BigWaveform engine={engine} phase={rec.phase} buffer={track ? bufOf(track.id) : null} under={instBuf} duration={duration} playhead={ph.time} onSeek={busyRec ? null : ph.seek}
            onSelect={busyRec || !activeTake ? null : (s) => setSel(snap ? snapRange(tempo, s) : s)} selection={sel} elapsed={rec.elapsed} grid={tempo} />
          {!live ? <PunchBar sel={sel} take={activeTake} preroll={preroll} onPreroll={setPreroll} onClear={() => setSel(null)} disabled={busyRec || syncing}
            tempo={tempo} snap={snap} onSnap={onSnap} onPunch={() => rec.start({ punch: { start: sel.start, end: sel.end, preroll } })} /> : null}
          <RecControls rec={rec} playing={ph.playing} onPlay={ph.play} onPausePlay={ph.pause} onStopPlay={ph.stop} onSave={onSave} saveState={saveState}
            settings={doc.settings} onSettings={(p) => change((d) => ({ ...d, settings: { ...d.settings, ...p } }))} headphones={setup.headphones === true} canRecord={Boolean(track) && !syncing} tempo={tempo} />
          <TakeCheckCard result={rec.result} saving={rec.saving} onRetrySave={rec.retrySave} onDismiss={rec.clearResult} />
          <TakesPanel session={session} track={track} revoice={revoice} />
        </div>
        <aside className="min-w-0 space-y-4 lg:col-span-2 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0 2xl:col-span-1 2xl:block 2xl:space-y-4">
          <div id="rec-section-sound"><VocalSoundPanel track={track} onChange={(p) => updateTrack(track.id, p)} /></div>
          <div id="rec-section-export" className="space-y-4"><ExportPanel key={doc.id} session={session} /><VoiceModelPanel session={session} /></div>
        </aside>
      </div>
      <LyricsSource open={lyricsOpen} onClose={() => setLyricsOpen(false)} lyrics={doc.lyrics} instrumental={doc.instrumental}
        onSave={(p) => { change((d) => ({ ...d, lyrics: { start_sec: 0, end_sec: null, ...(d.lyrics || {}), ...p } })); setLyricsOpen(false); }} />
    </CreatorShell>
  );
}
